/**
 * Custom theme colors — override de 12 jetons couleur × light/dark.
 *
 * @remarks
 * Port adapté du legacy `sass-front/src/store/useCustomColorsStore.ts` aux
 * conventions V2 (kebab-case, namespacing `saas-front-v2:*`, version + migrate
 * avec fallback safe).
 *
 * **Architecture** : utilise la cascade CSS en injectant un `<style>` element
 * unique avec deux blocs scopés (`:root:not([data-theme="dark"])` pour clair,
 * `[data-theme="dark"]` pour sombre). Quand l'utilisateur change le `primary`,
 * toute l'app qui consomme `var(--color-primary)` re-render gratis. Aucun
 * composant n'est modifié.
 *
 * **Anti-FOUC** : le bloc `if (typeof window !== 'undefined') applyCustomColors(...)`
 * en bas de fichier s'exécute au load du module. Il faut donc importer ce
 * module en side-effect dans `main.tsx` avant `createRoot().render()` pour
 * que les overrides soient appliqués avant le premier paint.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { textOnStrong } from '@/lib/utils/on-color';

export type ColorKey =
  | 'primary'
  | 'primary-hover'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'bg-primary'
  | 'bg-secondary'
  | 'bg-tertiary'
  | 'text-primary'
  | 'text-secondary'
  | 'border-primary';

export type ThemeColors = Partial<Record<ColorKey, string>>;

interface CustomColorsState {
  customColors: { light: ThemeColors; dark: ThemeColors };
  isCustomThemeEnabled: boolean;
}

interface CustomColorsActions {
  setCustomColor: (theme: 'light' | 'dark', key: ColorKey, value: string) => void;
  resetToDefaults: () => void;
  toggleCustomTheme: () => void;
  setCustomThemeEnabled: (enabled: boolean) => void;
}

type CustomColorsStore = CustomColorsState & CustomColorsActions;

// ── Defaults ───────────────────────────────────────────────────────────
//
// Valeurs synchronisées avec `tokens.css` (light + dark). Si tu modifies un
// défaut ici, modifie-le aussi dans `tokens.css` pour garder le toggle
// "enable custom theme" visuellement neutre tant que l'utilisateur n'a rien
// changé.

const DEFAULT_LIGHT_COLORS: ThemeColors = {
  primary: '#7c5cff',
  'primary-hover': '#9b82ff',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#3b82f6',
  'bg-primary': '#ffffff',
  'bg-secondary': '#f9fafb',
  'bg-tertiary': '#f3f4f6',
  'text-primary': '#111827',
  'text-secondary': '#374151',
  'border-primary': '#e5e7eb',
};

const DEFAULT_DARK_COLORS: ThemeColors = {
  primary: '#7c5cff',
  'primary-hover': '#9b82ff',
  success: '#34d399',
  warning: '#fbbf24',
  error: '#f87171',
  info: '#60a5fa',
  'bg-primary': '#0a0a0f',
  'bg-secondary': '#0f0f16',
  'bg-tertiary': '#1c1c26',
  'text-primary': '#f4f4f7',
  'text-secondary': '#d0d6e0',
  'border-primary': 'rgba(255, 255, 255, 0.08)',
};

const STYLE_ELEMENT_ID = 'custom-theme-colors';

/**
 * Variable CSS écrite pour chaque clé. Les clés neutres gardent leur nom
 * numéroté (valeurs déjà persistées chez les utilisateurs), mais pilotent le
 * token par rôle (SAAS-1181) : les variables numérotées n'existent plus.
 * Écrire l'ancien nom n'aurait aucun effet sur `fg` / `bg` /
 * `border`.
 */
const CSS_VAR_BY_KEY: Partial<Record<ColorKey, string>> = {
  'bg-primary': 'bg',
  'bg-secondary': 'bg-subtle',
  'bg-tertiary': 'bg-muted',
  'text-primary': 'fg',
  'text-secondary': 'fg-secondary',
  'border-primary': 'border',
};

/**
 * Part d'accent des fonds pleins d'action — les mêmes que les `color-mix` de
 * `tokens.css` (`--color-primary-strong`, `--color-error-strong`).
 */
const STRONG_MIX = {
  primary: { light: 0.88, dark: 0.88 },
  error: { light: 0.88, dark: 0.8 },
} as const;

/**
 * Texte des boutons pleins (SAAS-1185) : sur un accent personnalisé trop clair,
 * le blanc devient illisible — on bascule sur le quasi-noir.
 */
function onStrongRules(colors: ThemeColors, theme: 'light' | 'dark'): string[] {
  const rules: string[] = [];
  if (colors.primary) {
    rules.push(`  --color-on-primary: ${textOnStrong(colors.primary, STRONG_MIX.primary[theme])};`);
  }
  if (colors.error) {
    rules.push(`  --color-on-error: ${textOnStrong(colors.error, STRONG_MIX.error[theme])};`);
  }
  return rules;
}

/**
 * Crée/met à jour le `<style id="custom-theme-colors">` injecté en `<head>`.
 * Si `isEnabled` est `false`, supprime le style element (retour aux tokens
 * `tokens.css` natifs).
 */
function applyCustomColors(light: ThemeColors, dark: ThemeColors, isEnabled: boolean): void {
  if (typeof document === 'undefined') return;

  let style = document.getElementById(STYLE_ELEMENT_ID) as HTMLStyleElement | null;

  if (!isEnabled) {
    if (style) style.remove();
    return;
  }

  if (!style) {
    style = document.createElement('style');
    style.id = STYLE_ELEMENT_ID;
    document.head.appendChild(style);
  }

  const buildRules = (colors: ThemeColors, theme: 'light' | 'dark'): string =>
    [
      ...Object.entries(colors)
        .filter((entry): entry is [string, string] => Boolean(entry[1]))
        .map(([key, value]) => `  --color-${CSS_VAR_BY_KEY[key as ColorKey] ?? key}: ${value};`),
      ...onStrongRules(colors, theme),
    ].join('\n');

  style.textContent = `
/* Custom Theme Colors — Light */
:root:not([data-theme="dark"]) {
${buildRules(light, 'light')}
}

/* Custom Theme Colors — Dark */
[data-theme="dark"] {
${buildRules(dark, 'dark')}
}
`;
}

// ── Store ──────────────────────────────────────────────────────────────

export const useCustomColorsStore = create<CustomColorsStore>()(
  devtools(
    persist(
      (set, get) => ({
        customColors: {
          light: { ...DEFAULT_LIGHT_COLORS },
          dark: { ...DEFAULT_DARK_COLORS },
        },
        isCustomThemeEnabled: false,

        setCustomColor: (theme, key, value) => {
          const next = {
            ...get().customColors,
            [theme]: { ...get().customColors[theme], [key]: value },
          };
          applyCustomColors(next.light, next.dark, get().isCustomThemeEnabled);
          set({ customColors: next });
        },

        resetToDefaults: () => {
          const reset = {
            light: { ...DEFAULT_LIGHT_COLORS },
            dark: { ...DEFAULT_DARK_COLORS },
          };
          applyCustomColors(reset.light, reset.dark, get().isCustomThemeEnabled);
          set({ customColors: reset });
        },

        toggleCustomTheme: () => {
          const enabled = !get().isCustomThemeEnabled;
          const { customColors } = get();
          applyCustomColors(customColors.light, customColors.dark, enabled);
          set({ isCustomThemeEnabled: enabled });
        },

        setCustomThemeEnabled: (enabled) => {
          const { customColors } = get();
          applyCustomColors(customColors.light, customColors.dark, enabled);
          set({ isCustomThemeEnabled: enabled });
        },
      }),
      {
        name: 'saas-front-v2:custom-colors',
        version: 1,
        partialize: (state) => ({
          customColors: state.customColors,
          isCustomThemeEnabled: state.isCustomThemeEnabled,
        }),
        onRehydrateStorage: () => (state) => {
          if (state?.isCustomThemeEnabled) {
            applyCustomColors(state.customColors.light, state.customColors.dark, true);
          }
        },
        /**
         * Migration policy : 12 jetons × 2 modes + 1 toggle persisté. À chaque
         * ajout d'un jeton ou rename, bumper `version` et ajouter une branche.
         * Si payload corrompu → defaults pleins (l'utilisateur perd ses
         * customisations mais l'app reste utilisable).
         */
        // Aucune version antérieure n'existe : `migrate` n'est atteint que sur un
        // payload d'origine inconnue — on repart des palettes par défaut, custom
        // theme désactivé.
        // ⚠️ Au prochain bump (ajout ou rename d'un jeton), adapter AVANT ce retour
        // en comparant avec `<` : `if (oldVersion < 2) { …adapter… }`. Ne jamais
        // tester `oldVersion === <version courante>` : zustand n'appelle `migrate`
        // que si les versions diffèrent, la branche serait morte. Cascade de
        // référence : `use-layout-store`. (SAAS-903)
        migrate: () => ({
          customColors: {
            light: { ...DEFAULT_LIGHT_COLORS },
            dark: { ...DEFAULT_DARK_COLORS },
          },
          isCustomThemeEnabled: false,
        }),
      },
    ),
    { name: 'custom-colors', enabled: import.meta.env.DEV },
  ),
);

// Auto-init au load du module : si l'utilisateur avait activé les custom
// colors, on les applique immédiatement avant React mount (anti-FOUC).
// `main.tsx` doit importer ce module en side-effect pour déclencher ce bloc.
if (typeof window !== 'undefined') {
  const initial = useCustomColorsStore.getState();
  if (initial.isCustomThemeEnabled) {
    applyCustomColors(initial.customColors.light, initial.customColors.dark, true);
  }
}
